/// <amd-module name="Test/TestPlansHubRefresh/TestRunsMenu/TestRunsProviderService" />
import * as React from "react";
import * as ExecuteConstants from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/ExecuteConstants";
import { PointsActions } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsActions";
import { ITestPointItem, RunOptions } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import * as ExecuteResources from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Resources";
import { TelemetryConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { testHubRefreshLocalContextData } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import * as Resources from "Test/TestPlansHubRefresh/TestRunsMenu/Resources";
import {
    IBulkMarkPointsMenu,
    IExecutePivotContextMenuParams,
    ITestRunsMenu,
    ITestRunsMenuExtended
} from "Test/TestPlansHubRefresh/TestRunsMenu/TestRunsMenu.Types";
import { Outcome, TestPointUpdateParams } from "TestPlanCommon/Contracts/TestPlan";
import { ObservableValue } from "VSS/Core/Observable";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { IdentityPickerDropdown, IIdentity, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { AssignTesterPeoplePickerProvider } from "./AssignTesterPeoplePickerProvider";

// This class is a provider service class to add the Assign Tester option for advance license users and its capabililty
export class TestRunsProviderService extends VssService implements IMenuService {
    private _pickerProvider: IPeoplePickerProvider;
    private _selectedUser: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);
    private _props: IExecutePivotContextMenuParams;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._pickerProvider = new AssignTesterPeoplePickerProvider(pageContext, {
            identityTypes: [ExecuteConstants.User],
            operationScopes: [ExecuteConstants.Scope]
        });
        this.pageContext = pageContext;
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
    }

    public getMenuItems(menuId: string, props: IExecutePivotContextMenuParams): IMenuItem[] | null {
        this._props = props;
        const menuItems: IMenuItem[] = [];

        if (menuId === "ms.vss-test-web.test-point-context-menu") {
            // view execution History
            if (props.selection.selectedCount === 1) {
                menuItems.push({
                    id: "view-execution-history",
                    text: ExecuteResources.ViewExecutionHistory,
                    iconProps: { iconName: ExecuteConstants.ViewExecutionHistoryIconName, className: "history-icon" },
                    onActivate: (menuItem: IMenuItem) => {
                        if (this._props.openTestCaseResultsPanel) {
                            this._props.openTestCaseResultsPanel();
                        }
                    }
                });
            }

            // mark outcome of a test
            menuItems.push({
                id: "mark-outcome",
                text: ExecuteResources.MarkOutcome,
                iconProps: { iconName: ExecuteConstants.MarkOutcomeIconName, className: "checkmark-icon" },
                subMenuProps: {
                    id: "mark-outcome-submenu",
                    items: [
                        {
                            id: "pass-test",
                            text: ExecuteResources.PassTest,
                            iconProps: { iconName: ExecuteConstants.PassTestIconName, className: "pass-icon" },
                            onActivate: (menuItem: IMenuItem) => {
                                this.bulkMarkPass(props);
                            }
                        },
                        {
                            id: "fail-test",
                            text: ExecuteResources.FailTest,
                            iconProps: { iconName: ExecuteConstants.FailTestIconName, className: "fail-icon" },
                            onActivate: (menuItem: IMenuItem) => {
                                this.bulkMarkFail(props);
                            }
                        },
                        {
                            id: "block-test",
                            text: ExecuteResources.BlockTest,
                            iconProps: { iconName: ExecuteConstants.BlockTestIconName, className: "block-icon" },
                            onActivate: (menuItem: IMenuItem) => {
                                this.bulkMarkBlock(props);
                            }
                        },
                        {
                            id: "not-applicable",
                            text: ExecuteResources.NotApplicable,
                            iconProps: { iconName: ExecuteConstants.NotApplicableIconName, className: "not-applicable-icon" },
                            onActivate: (menuItem: IMenuItem) => {
                                this.bulkMarkNotApplicable(props);
                            }
                        }
                    ]
                }
            });
            // menu item for test run
            menuItems.push({
                id: "run-menu",
                text: ExecuteResources.RunTests,
                iconProps: { iconName: ExecuteConstants.RunIconName, className: "play-icon" },
                subMenuProps: {
                    id: "run-test-sub-menu",
                    items: [
                        {
                            id: "run-for-web-application",
                            text: ExecuteResources.RunForWebApplication,
                            onActivate: (menuItem: IMenuItem) => {
                                this.launchWebRunner(props);
                            }
                        },
                        {
                            id: "run-for-desktop-application",
                            text: ExecuteResources.RunForDesktop,
                            onActivate: (menuItem: IMenuItem) => {
                                this.launchTestRunner(props);
                            }
                        },
                        {
                            id: "run-with-options",
                            text: ExecuteResources.RunWithOptions,
                            onActivate: (menuItem: IMenuItem) => {
                                this.launchRunWithOptionsDialog(props);
                            }
                        }
                    ]
                }
            });
            // menu item for resetting test to active state
            menuItems.push({
                id: "reset-test-to-active",
                text: ExecuteResources.ResetTestToActive,
                iconProps: { iconName: ExecuteConstants.ResetTestToActiveIconName, className: "refresh-icon" },
                onActivate: (menuItem: IMenuItem) => {
                    this.bulkMarkActive(props);
                }
            });

            menuItems.push({
                id: "run-edit-test-separator",
                itemType: MenuItemType.Divider
            });

            if (props.selection.selectedCount === 1) {
                // menu item to edit a single test case
                menuItems.push({
                    id: "edit-test-case",
                    text: ExecuteResources.EditTestCase,
                    iconProps: { iconName: ExecuteConstants.EditTestIconName, className: "edit-icon" },
                    onActivate: (menuItem: IMenuItem) => {
                        this.editTestCase(props);
                    }
                });
            }

            if (props.isAdvancedLicenseEnabled) {
                if (props.selection.selectedCount > 1) {
                    menuItems.push({
                        id: "edit-test-cases",
                        text: ExecuteResources.EditTestCases,
                        iconProps: { iconName: ExecuteConstants.EditTestIconName, className: "edit-icon" },
                        onActivate: (menuItem: IMenuItem) => {
                            this.bulkEditTestCases(props);
                        }
                    });
                }

                menuItems.push({
                    id: "assign-tester",
                    text: ExecuteResources.AssignTesterMenuItemText,
                    iconProps: { iconName: ExecuteConstants.AssignTesterIconName },
                    subMenuProps: {
                        id: "assign-tester-submenu",
                        items: [
                            {
                                id: "identity-picker",
                                // As we need to open the Assign tester dropdown and capability as a submenu item,
                                // we have used the method renderMenuItem to render the identity picker dropdown as a submenu
                                // PickerProvider : Provider for getting the and managing the list of identities
                                // autoFocus: keeps the dropdown open initially
                                // calloutWidth: width of the callout displaying the list of identities
                                // editPlaceholder: placeholder text for the dropdown text field
                                // onChange: called when an user is selected
                                // value: get the value of the currently selected user
                                renderMenuItem: () => {
                                    return (
                                        <div onKeyDown={this._onKeyDown}>
                                            <IdentityPickerDropdown
                                                pickerProvider={this._pickerProvider}
                                                autoFocus={true}
                                                calloutWidth={50}
                                                editPlaceholder={ExecuteResources.IdentityPickerPlaceholderText}
                                                onChange={this._onTesterSelection.bind(this)}
                                                value={this._selectedUser}
                                            />
                                        </div>
                                    );
                                }
                            }
                        ]
                    }
                });
            }

            menuItems.push({
                id: "edit-view-test-separator",
                itemType: MenuItemType.Divider
            });

            if (props.selection.selectedCount === 1) {
                const index = this._props.selection.value[0].beginIndex;

                if (props.points[index].lastResultId > 0 && props.points[index].lastRunId > 0) {
                    menuItems.push({
                        id: "view-test-results",
                        iconProps: { iconName: ExecuteConstants.ViewTestResultIconName },
                        text: ExecuteResources.ViewTestResult,
                        onActivate: (menuItem: IMenuItem) => {
                            this._props.dispatch(
                                PointsActions.openTestResult(props.points[index].id, props.points[index].lastRunId, props.points[index].lastResultId)
                            );
                        },
                        data: props
                    });
                }
                
                // Add resume test menu item if only 1 point is selected and outcome of point is paused
                if (props.points[index].outcomeItem && props.points[index].outcomeItem.outcome === Outcome.Paused) {
                    menuItems.push({
                        id: "resume-test-menu-item",
                        iconProps: { iconName: ExecuteConstants.ResumeTestIconName },
                        text: ExecuteResources.ResumeTestMenuItemText,

                        subMenuProps: {
                            id: "run-test-sub-menu",
                            items: [
                                {
                                    id: "resume-for-web-application",
                                    text: ExecuteResources.ResumeTestForWebApplication,
                                    onActivate: (menuItem: IMenuItem) => {
                                        /**
                                            * Point Ids which are invoked in web runner are stored in local context
                                            * this is done to refresh points in UI when web runner is closed.
                                            */
                                        const testPointToResume = props.points[index];
                                        testHubRefreshLocalContextData.setWebRunnerPointIds([testPointToResume.id]);
                                        this._props.dispatch(PointsActions.resumeTest(testPointToResume, props.planId, props.suiteId));
                                    },
                                },
                                {
                                    id: "resume-for-desktop-application",
                                    text: ExecuteResources.ResumeTestForDesktopApplication,
                                    onActivate: (menuItem: IMenuItem) => {
                                        const testPointToResume = props.points[index];
                                        this.launchTestRunnerToResumeTest(props, testPointToResume);
                                    }
                                }
                            ]
                        },
                        data: props
                    });

                }
            }
        }
        return menuItems;
    }

    public _onTesterSelection = (selectedTester?: IIdentity): void => {
        if (selectedTester) {
            const updateParams: TestPointUpdateParams[] = [];
            const selectionLength = this._props.selection.value.length;
            if (selectionLength) {
                for (let i = 0; i < selectionLength; i++) {
                    // Create updateParams for all the selected test points
                    for (let j = this._props.selection.value[i].beginIndex; j <= this._props.selection.value[i].endIndex; j++) {
                        const updateParam: TestPointUpdateParams = {} as TestPointUpdateParams;
                        if (this._props.points[j]) {
                            updateParam.id = this._props.points[j].id;
                            if (selectedTester.displayName === ExecuteResources.UnassignedIdentity) {
                                updateParam.tester = {
                                    id: ExecuteConstants.UnassignedIdentityGuid
                                };
                            } else {
                                updateParam.tester = {
                                    id: selectedTester.localId,
                                    displayName: selectedTester.displayName
                                };
                            }
                            updateParams.push(updateParam);
                        }
                    }
                }
                if (updateParams.length > 0) {
                    // This ensures that the dropdown get hidden after user selects an user
                    this._props.dispatch(PointsActions.updateAssignTesterDropdownState(false));
                    // This dispatches the Action to update the tester
                    this._props.dispatch(PointsActions.updateTestPointsForSelectedSuite(updateParams, this._props.planId, this._props.suiteId));
                    const telemetryPayload = {
                        planId: this._props.planId,
                        suiteId: this._props.suiteId,
                        updatedTestPointsCount: updateParams.length,
                        testerAssigned: selectedTester.displayName,
                        isAdvancedLicenseEnabled: this._props.isAdvancedLicenseEnabled
                    };
                    // This dispatches a telemetry action
                    this._props.dispatch(TelemetryActions.publishTelemetry(TelemetryConstants.BULK_MARK_ASSIGN_TESTER, telemetryPayload));
                }
            } else {
                // if no test point is selected show an alert prompt
                alert(ExecuteResources.NoItemSelected);
            }
        }
    };

    public launchTestRunner(params: ITestRunsMenuExtended): void {
        const selectedtestPoints: ITestPointItem[] = [];
        const selectionLength = params.selection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = params.selection.value[i].beginIndex; j <= params.selection.value[i].endIndex; j++) {
                selectedtestPoints.push(params.points[j]);
            }
        }

        if (selectedtestPoints.length > 0) {
            const runParams: ITestRunsMenu = {
                planAreaPath: params.planAreaPath,
                planId: params.planId,
                planIteration: params.planIteration,
                planName: params.planName,
                points: selectedtestPoints,
                suiteId: params.suiteId,
                suiteName: params.suiteName,
                selectedBuild: params.selectedBuild
            };
            params.dispatch(PointsActions.openApplicationTestRunnerClick(runParams));
        } else {
            alert(Resources.NoItemSelected);
        }
    }

    /* This method is used to initiate the launch of test runner to resume selected test point
     * Dispatched action openApplicationTestRunnerClickToResumeTest indicates that user has clicked
     * on submenu option to resume the test run
     * Subsequently openApplicationTestRunnerToResumeTest is dispatched to launch the test runner
     * Although only 1 test point can be resumed, ITestRunsMenu object has points array as an attribute
     * */
    public launchTestRunnerToResumeTest(params: ITestRunsMenuExtended, testPointToResume: ITestPointItem): void {
        const runParams: ITestRunsMenu = {
            planAreaPath: params.planAreaPath,
            planId: params.planId,
            planIteration: params.planIteration,
            planName: params.planName,
            points: [testPointToResume],
            suiteId: params.suiteId,
            suiteName: params.suiteName,
            selectedBuild: params.selectedBuild
        };
        params.dispatch(PointsActions.openApplicationTestRunnerClickToResumeTest(runParams));
    }


    public launchWebRunner(params: ITestRunsMenuExtended): void {
        const selectedtestPoints: ITestPointItem[] = [];
        const selectionLength = params.selection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = params.selection.value[i].beginIndex; j <= params.selection.value[i].endIndex; j++) {
                selectedtestPoints.push(params.points[j]);
            }
        }

        if (selectedtestPoints.length > 0) {
            const runParams: ITestRunsMenu = {
                planAreaPath: params.planAreaPath,
                planId: params.planId,
                planIteration: params.planIteration,
                planName: params.planName,
                points: selectedtestPoints,
                suiteId: params.suiteId,
                suiteName: params.suiteName,
                runOptions: RunOptions.RunTestUsingWebRunner,
                selectedBuild: params.selectedBuild
            };

            /**
             * Point Ids which are invoked in web runner are stored in local context
             * this is done to refresh points in UI when web runner is closed.
             */
            const testPointIds = selectedtestPoints.map((points) => points.id);
            testHubRefreshLocalContextData.setWebRunnerPointIds(testPointIds);

            params.dispatch(PointsActions.openTestPointsRunnerClient(runParams));
        } else {
            alert(Resources.NoItemSelected);
        }
    }

    public launchRunWithOptionsDialog(params: IExecutePivotContextMenuParams): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            return React.createElement(
                WrappedComponent,
                {
                    wrappedType: "RunWithOptionsDialog",
                    dependencies: ["ms.vss-test-web.run-with-options-dialog"],
                    wrappedProps: {
                        params: params,
                        onDismiss: onDismiss
                    }
                },
                null
            );
        });
    }

    public bulkEditTestCases(params: IExecutePivotContextMenuParams): void {
        let testCaseIds: number[] = [];
        const testPointIds = params.points.map((points) => points.id);
        const selectionLength = params.selection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = params.selection.value[i].beginIndex; j <= params.selection.value[i].endIndex; j++) {
                const testCaseId = params.points[j].testCaseRef.id;
                testCaseIds.push(testCaseId);
            }
        }
        // above testCaseId can have duplicates
        testCaseIds = [...new Set(testCaseIds)];
        params.dispatch(PointsActions.bulkEditTestCases(testPointIds, testCaseIds, params.planId, params.suiteId));
    }

    public editTestCase(params: IExecutePivotContextMenuParams): void {
        if (params.selection.selectedCount == 1) {
            const testCaseIndex = params.selection.value[0].beginIndex;
            const testCaseId: number = params.points[testCaseIndex].testCaseRef.id;
            const testCaseTitle: string = params.points[testCaseIndex].testCaseRef.title;
            const testCaseState: string = params.points[testCaseIndex].testCaseRef.state;

            let testCaseAssignedTo = "";
            if (params.points[testCaseIndex].testCaseRef.assignedTo && params.points[testCaseIndex].testCaseRef.assignedTo.displayName) {
                testCaseAssignedTo = params.points[testCaseIndex].testCaseRef.assignedTo.displayName;
            }

            params.dispatch(
                PointsActions.openExistingTestCaseWitForm(
                    params.planId,
                    params.suiteId,
                    testCaseId,
                    testCaseTitle,
                    testCaseState,
                    testCaseAssignedTo,
                    params.points
                )
            );
        }
    }

    public bulkMarkPass(params: IBulkMarkPointsMenu): void {
        this._updateTestPointResult(params, Outcome.Passed, false);
    }

    public bulkMarkFail(params: IBulkMarkPointsMenu): void {
        this._updateTestPointResult(params, Outcome.Failed, false);
    }

    public bulkMarkBlock(params: IBulkMarkPointsMenu): void {
        this._updateTestPointResult(params, Outcome.Blocked, false);
    }

    public bulkMarkNotApplicable(params: IBulkMarkPointsMenu): void {
        this._updateTestPointResult(params, Outcome.NotApplicable, false);
    }

    public bulkMarkActive(params: IBulkMarkPointsMenu): void {
        this._updateTestPointResult(params, Outcome.None, true);
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // This is to ensure that all key events are being received by the textbox field in the assign tester dropdown
        event.stopPropagation();
    };

    private _updateTestPointResult(params: IBulkMarkPointsMenu, outcome: Outcome, isActive: boolean): void {
        const updateParams: TestPointUpdateParams[] = [];
        const selectionLength = params.selection.value.length;
        for (let i = 0; i < selectionLength; i++) {
            for (let j = params.selection.value[i].beginIndex; j <= params.selection.value[i].endIndex; j++) {
                if (params.points[j]) {
                    const updateParam: TestPointUpdateParams = {} as TestPointUpdateParams;
                    updateParam.id = params.points[j].id;
                    if (isActive == true) {
                        updateParam.isActive = true;
                    } else {
                        updateParam.results = { outcome: outcome };
                    }
                    updateParams.push(updateParam);
                }
            }
        }

        if (updateParams.length > 0) {
            params.dispatch(PointsActions.updateTestPointsForSelectedSuite(updateParams, params.planId, params.suiteId));
            const telemetryPayload: { [key: string]: any } = {
                planId: params.planId,
                suiteId: params.suiteId,
                updatedTestPointsCount: updateParams.length,
                isAdvancedLicenseEnabled: params.isAdvancedLicenseEnabled
            };
            if (isActive) {
                telemetryPayload.isActive = true;
            } else {
                telemetryPayload.outcome = updateParams[0].results.outcome;
            }

            params.dispatch(TelemetryActions.publishTelemetry(TelemetryConstants.BULK_MARK_TEST_OUTCOME, telemetryPayload));
        } else {
            alert(Resources.NoItemSelected);
        }
    }
}

Services.add("points-execute-menu-provider", { serviceFactory: TestRunsProviderService });
